import React from 'react';
import * as R from 'ramda';
// components
import { TextComponent } from '../../../../components/text';
import { ActionBox } from '../../../../components/action-box';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import { EXPENSE_PARTY_OPTIONS, EXPENSE_CHARGE_TYPE_OPTIONS } from '../../../../helpers/options';
// utilities
import routesMap from '../../../../utilities/routes';
// feature fleet-profile
import ExpenseForm from '../forms/expense-form';
import { TableCell } from '../../components/table-cells';
import ExpenseDocument from '../components/expense-document';
import ApproveDeclineExpense from '../components/approve-decline-expense';
//////////////////////////////////////////////////

const getTruckInfo = (truck) => `${R.prop(GC.FIELD_UNIT_ID, truck)} (${
  R.path([GC.FIELD_TRUCK_TYPE, GC.FIELD_DISPLAYED_VALUE], truck)})`;

const getTrailerInfo = (trailer) => `${R.prop(GC.FIELD_UNIT_ID, trailer)} (${
  R.path([GC.FIELD_TRAILER_TYPE, GC.FIELD_DISPLAYED_VALUE], trailer)})`;

const getLocationFieldFilter = (fieldName) => ({
  type: 'string',
  getFieldUiValue: ({ location }) => {
    if (G.isNilOrEmpty(location)) return '';

    return R.pathOr('', [fieldName], location);
  },
});

export const columnSettings = {
  [GC.FIELD_STATUS]: {
    width: 150,
    sortable: false,
    searchable: false,
    notResizable: true,
    name: 'titles:status',
    customComponent: ({ data, callbackData: { isReadOnlyMode, approveOrDeclineDriverExpenseRequest } }) => (
      <ApproveDeclineExpense
        {...data}
        isReadOnlyMode={isReadOnlyMode}
        approveOrDeclineDriverExpenseRequest={approveOrDeclineDriverExpenseRequest}
      />
    ),
  },
  [GC.FIELD_TYPE]: {
    width: 150,
    name: 'titles:expense-type',
    customComponent: ({ data }) => R.path([GC.FIELD_TYPE, GC.FIELD_NAME], data),
    filter: {
      type: 'string',
      getFieldUiValue: R.path([GC.FIELD_TYPE, GC.FIELD_NAME]),
    },
  },
  [GC.FIELD_NEGATIVE]: {
    width: 150,
    type: 'boolean',
    name: 'titles:deduction',
    filter: { type: 'boolean' },
  },
  [GC.FIELD_TOTAL]: {
    width: 100,
    name: 'titles:total',
    filter: { type: 'number' },
    customComponent: ({ data }) => {
      const { total, currency } = data;

      if (R.isNil(total)) return null;

      return `${G.getCurrencySymbol(currency)} ${total}`;
    },
  },
  [GC.FIELD_FILE_NAME]: {
    width: 200,
    name: 'titles:file',
    customComponent: ({ data, callbackData: { isReadOnlyMode } }) =>
      <ExpenseDocument {...data} hideDelete={isReadOnlyMode} />,
  },
  [GC.FIELD_TRUCK]: {
    width: 150,
    name: 'titles:truck',
    filter: {
      type: 'string',
      getFieldUiValue: ({ truck }) => {
        if (G.isNilOrEmpty(truck)) return '';

        return getTruckInfo(truck);
      },
    },
    customComponent: ({ data }) => {
      const { truck } = data;

      if (R.isNil(truck)) return null;

      const text = getTruckInfo(truck);

      return (
        <TextComponent
          title={text}
          maxWidth='90%'
          display='block'
          withEllipsis={true}
        >
          {text}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_TRAILER]: {
    width: 150,
    name: 'titles:trailer',
    filter: {
      type: 'string',
      getFieldUiValue: ({ trailer }) => {
        if (G.isNilOrEmpty(trailer)) return '';

        return getTrailerInfo(trailer);
      },
    },
    customComponent: ({ data }) => {
      const { trailer } = data;

      if (R.isNil(trailer)) return null;

      const text = getTrailerInfo(trailer);

      return (
        <TextComponent
          title={text}
          maxWidth='90%'
          display='block'
          withEllipsis={true}
        >
          {text}
        </TextComponent>
      );
    },
  },
  [GC.FIELD_EXPENSE_CHARGE_TYPE]: {
    width: 180,
    type: 'enumLocale',
    name: 'titles:charge-type',
    filter: {
      type: 'selectMultiple',
      options: EXPENSE_CHARGE_TYPE_OPTIONS,
    },
  },
  [GC.FIELD_EXPENSE_PARTY]: {
    width: 180,
    type: 'enumLocale',
    name: 'titles:expense-party',
    filter: {
      type: 'selectMultiple',
      options: EXPENSE_PARTY_OPTIONS,
    },
  },
  [GC.FIELD_DATE]: {
    type: 'date',
    name: 'titles:date',
    filter: { type: 'date' },
  },
  [GC.GRC.LOCATION_NAME]: {
    width: 180,
    name: ['titles:location', 'titles:name'],
    filter: getLocationFieldFilter(GC.FIELD_LOCATION_NAME),
    customComponent: ({ data }) => (
      <TableCell text={R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_LOCATION_NAME], data)} />
    ),
  },
  [GC.GRC.LOCATION_ADDRESS]: {
    name: ['titles:location', 'titles:address'],
    filter: getLocationFieldFilter(GC.FIELD_ADDRESS),
    customComponent: ({ data }) => (
      <TableCell text={R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_ADDRESS], data)} />
    ),
  },
  [GC.GRC.LOCATION_CITY]: {
    width: 180,
    name: ['titles:location', 'titles:city'],
    filter: getLocationFieldFilter(GC.FIELD_CITY),
    customComponent: ({ data }) => (
      <TableCell text={R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_CITY], data)} />
    ),
  },
  [GC.GRC.LOCATION_STATE]: {
    width: 180,
    name: ['titles:location', 'titles:state'],
    filter: getLocationFieldFilter(GC.FIELD_STATE),
    customComponent: ({ data }) => (
      <TableCell text={R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_STATE], data)} />
    ),
  },
  [GC.GRC.LOCATION_COUNTRY]: {
    width: 180,
    name: ['titles:location', 'titles:country'],
    filter: getLocationFieldFilter(GC.FIELD_COUNTRY),
    customComponent: ({ data }) => (
      <TableCell text={R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_COUNTRY], data)} />
    ),
  },
  [GC.GRC.LOCATION_ZIP]: {
    width: 180,
    name: ['titles:location', 'titles:zip'],
    filter: getLocationFieldFilter(GC.FIELD_ZIP),
    customComponent: ({ data }) => (
      <TableCell text={R.path([GC.SYSTEM_OBJECT_LOCATION, GC.FIELD_ZIP], data)} />
    ),
  },
  [GC.FIELD_DESCRIPTION]: {
    width: 250,
    name: 'titles:description',
  },
  [GC.FIELD_PAYROLL_NUMBER]: {
    width: 180,
    name: 'titles:payroll-number',
    filter: {
      type: 'string',
      getFieldUiValue: R.path([GC.SYSTEM_OBJECT_DRIVER_PAYROLL, GC.FIELD_PAYROLL_NUMBER]),
    },
    customComponent: ({ data }) =>
      <ActionBox
        action={() => G.goToRoute(routesMap.driverPayrollList)}
        text={R.path([GC.SYSTEM_OBJECT_DRIVER_PAYROLL, GC.FIELD_PAYROLL_NUMBER], data)}
      />,
  },
  [GC.FIELD_CREATED_DATE]: {
    type: 'date',
    filter: { type: 'date' },
    name: 'titles:created-on',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 150,
    name: 'titles:created-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    type: 'date',
    filter: { type: 'date' },
    name: 'titles:update-on',
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 150,
    name: 'titles:updated-by',
  },
};

const defaultReportFields = R.keys(columnSettings);

const report = {
  [GC.FIELD_TYPE]: GC.DRIVER_PROFILE_UI_REPORTS,
  [GC.FIELD_GUID]: `${GC.DRIVER_PROFILE_UI_REPORTS}Expense`,
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), defaultReportFields),
};

const getRemoveOrEditPermissions = ({ payrollGuid }) => {
  if (G.isNotNil(payrollGuid)) return '';
};

export const expenseSettings = {
  report,
  columnSettings,
  defaultReportFields,
  withResizableColumns: true,
  groupName: 'driverExpenseList',
  withUiFiltersAndEditView: true,
  getItemListRequestMethod: 'post',
  actionsPicker: ['edit', 'remove'],
  itemTitleArr: ['titles:expense', 'Expense'],
  getEditPermissions: getRemoveOrEditPermissions,
  disableFieldHandler: R.equals(GC.FIELD_STATUS),
  getRemovePermissions: getRemoveOrEditPermissions,
  formGroupTitleArr: ['titles:expense', 'Expense'],
  tableCallbackDataProps: ['isReadOnlyMode', 'approveOrDeclineDriverExpenseRequest'],
  endpoints: {
    list: 'driverExpenseList',
    remove: 'driverExpenseByGuid',
    createOrUpdate: 'driverExpense',
  },
  CustomForm: (props) => <ExpenseForm {...props} />,
  customCreateOrUpdateHandler: ({ values, createOrUpdateDriverExpenseRequest }) =>
    createOrUpdateDriverExpenseRequest(values),
  modalAdditionalOptions: G.getFullWindowModalPropsWithLeftSpace(400),
  makeRequestPayload: ({ driverBranchGuid, primaryObjectGuid }) => ({
    params: {
      [GC.FIELD_DRIVER_GUID]: primaryObjectGuid,
    },
    data: {
      pageable: false,
      [GC.CURRENT_BRANCH]: driverBranchGuid,
    },
  }),
  makeInitialValues: (initialValues, props) => ({
    ...initialValues,
    [GC.FIELD_BRANCH_GUID]: G.getPropFromObject('driverBranchGuid', props),
    [GC.FIELD_EXPENSE_TYPE_GUID]: R.pathOr(null, [GC.FIELD_TYPE, GC.FIELD_GUID], initialValues),
  }),
};
